import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/App";
import { UI, tr } from "@/i18n";
import PreliminaryReport from "@/components/PreliminaryReport";
import { ChevronRight, ArrowRight, FileText } from "lucide-react";
import { track as gaTrack } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportPage = () => {
  const { token } = useParams();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paDone, setPaDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/onematch/report/${token}`)
      .then((r) => {
        setData(r.data); setPaDone(r.data.personalAssessmentRequested);
        const l = r.data.assessment?.lang;
        if (l && ["en", "tr", "es"].includes(l)) setLang(l);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]); // eslint-disable-line

  const track = (cta) => { axios.post(`${API}/onematch/report/${token}/cta`, { cta }).catch(() => {}); };
  const unlock = () => { track("unlock_report"); gaTrack("detailed_report_clicked", { from: "report_page" }); navigate(`/onematch/report/${token}/detailed`); };
  const consult = async (type) => {
    gaTrack("consultation_clicked", { consult_type: type });
    track(type === "intro" ? "intro_call" : "paid_consult");
    try {
      const r = await axios.post(`${API}/onematch/report/${token}/consultation-intent`, { type });
      if (r.data.bookingUrl) window.open(r.data.bookingUrl, "_blank", "noopener");
      else { setPaDone(true); toast.success(tr(lang, UI.intent_recorded)); }
    } catch (e) { toast.error(tr(lang, UI.error_generic)); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">…</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground" data-testid="report-not-found">{tr(lang, UI.report_not_found)}</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <PreliminaryReport assessment={data.assessment} firstName={data.firstName} />

      <button data-testid="report-unlock-detailed" onClick={unlock}
        className="mt-10 w-full text-left bg-navy-deep text-white rounded-sm p-6 sm:p-7 relative om-grain hover:bg-navy transition-colors duration-300 group">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-gold mb-1"><FileText className="w-4 h-4" /><span className="overline">{tr(lang, UI.report_head)}</span></div>
            <h3 className="font-display text-2xl sm:text-3xl">{tr(lang, UI.cta_unlock)}</h3>
            <p className="mt-2 text-white/70 text-sm leading-relaxed max-w-xl">{tr(lang, UI.cta_unlock_sub)}</p>
          </div>
          <ArrowRight className="w-6 h-6 text-gold shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </button>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <button data-testid="report-paid-consult" onClick={() => consult("consult")}
          className="text-left bg-white border-2 border-navy rounded-sm p-5 hover:bg-cream transition-colors duration-300">
          <span className="text-[10px] uppercase tracking-wide bg-navy text-white rounded-full px-2 py-0.5">{tr(lang, UI.badge_paid)}</span>
          <h3 className="font-display text-xl text-navy-deep mt-2">{tr(lang, UI.cta_consult)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{tr(lang, UI.cta_consult_sub)}</p>
        </button>
        <button data-testid="report-intro-call" onClick={() => consult("intro")}
          className="text-left bg-transparent border border-border rounded-sm p-5 hover:border-navy/50 transition-colors duration-300">
          <span className="text-[10px] uppercase tracking-wide border border-border text-muted-foreground rounded-full px-2 py-0.5">{tr(lang, UI.badge_free)}</span>
          <h3 className="font-medium text-navy-deep mt-2">{tr(lang, UI.cta_intro)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{tr(lang, UI.cta_intro_sub)}</p>
        </button>
      </div>
      {paDone && <p data-testid="pa-confirm" className="mt-4 text-gold font-medium text-sm">{tr(lang, UI.intent_recorded)}</p>}

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{tr(lang, UI.report_disclaimer)}</p>
    </div>
  );
};

export default ReportPage;
